import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { MapLayer } from "@iventis/domain-model/model/mapLayer";
import { Node } from "@iventis/domain-model/model/node";
import { StyleType } from "@iventis/domain-model/model/styleType";
import { SystemDataFieldName } from "@iventis/domain-model/model/systemDataFieldName";
import { createMapFromArray } from "@iventis/utilities";

/**
 * Returns the datafield for map object name
 * @Note SystemDataFieldName enums are only ever on project datafields
 */
export const getMapObjectNameSystemDatafield = (projectDatafields: DataField[]) => projectDatafields?.find((df) => df.systemDataFieldName === SystemDataFieldName.MapObjectName);

/**
 * Returns the datafield for Area, only ever on a mapobject with geometry type Polygon
 * @Note SystemDataFieldName enums are only ever on project datafields
 */
export const getMapObjectAreaSystemDataField = (projectDatafields: DataField[]) => projectDatafields?.find((df) => df.systemDataFieldName === SystemDataFieldName.MapObjectArea);

/**
 * Returns the datafield for Length, only ever on a mapobject with geometry type LineString
 * @Note SystemDataFieldName enums are only ever on project datafields
 */
export const getMapObjectLengthSystemDataField = (projectDatafields: DataField[]) =>
    projectDatafields?.find((df) => df.systemDataFieldName === SystemDataFieldName.MapObjectLength);

export const getMapObjectCoordinatesSystemDataField = (projectDatafields: DataField[]) =>
    projectDatafields?.find((df) => df.systemDataFieldName === SystemDataFieldName.Coordinates);

export const getMapObjectRouteTimeSystemDataField = (projectDatafields: DataField[]) => projectDatafields?.find((df) => df.systemDataFieldName === SystemDataFieldName.RouteTime);

/**
 * Returns the datafield for Quantity of models on a line, only ever on a mapobject with geometry type LineModel
 * @Note SystemDataFieldName enums are only ever on project datafields
 */
export const getMapObjectQuantityOfModelsOnLineSystemDataField = (projectDatafields: DataField[]) =>
    projectDatafields?.find((df) => df.systemDataFieldName === SystemDataFieldName.QuantityOfModelsOnLine);

/**
 * Returns the datafield for Quantity of models in an area, only ever on a mapobject with geometry type AreaModel
 * @Note SystemDataFieldName enums are only ever on project datafields
 */
export const getMapObjectQuantityOfModelsInAreaSystemDataField = (projectDatafields: DataField[]) =>
    projectDatafields?.find((df) => df.systemDataFieldName === SystemDataFieldName.QuantityOfModelsInArea);

/**
 * Returns the datafield for Rotation of models in an area, only ever on a mapobject with geometry type AreaModel
 * @Note SystemDataFieldName enums are only ever on project datafields
 */
export const getMapObjectRotationZOfModelsInAreaSystemDataField = (projectDatafields: DataField[]) =>
    projectDatafields?.find((df) => df.systemDataFieldName === SystemDataFieldName.RotationZOfModelsInArea);

/**
 * Returns the datafield for Rotation of Icons, only ever on a mapobject with geometry type Icon
 * @Note SystemDataFieldName enums are only ever on project datafields
 */
export const getMapObjectIconRotationSystemDataField = (projectDatafields: DataField[]) =>
    projectDatafields?.find((df) => df.systemDataFieldName === SystemDataFieldName.IconRotation);

export const getModelAltitudeSystemDataField = (projectDataFields: DataField[]) => projectDataFields?.find((df) => df.systemDataFieldName === SystemDataFieldName.ModelAltitude);

export const getRelaySystemDataField = (projectDataFields: DataField[]) => projectDataFields?.find((df) => df.systemDataFieldName === SystemDataFieldName.Relay);

export const getRelayDaySystemDataField = (projectDataFields: DataField[]) => projectDataFields?.find((df) => df.systemDataFieldName === SystemDataFieldName.RelayDay);

export const getRelayStageSystemDataField = (projectDataFields: DataField[]) => projectDataFields?.find((df) => df.systemDataFieldName === SystemDataFieldName.RelayStage);

export const getRelayStreamSystemDataField = (projectDataFields: DataField[]) => projectDataFields?.find((df) => df.systemDataFieldName === SystemDataFieldName.RelayStream);

const relaySystemDataFieldNames = new Set([SystemDataFieldName.Relay, SystemDataFieldName.RelayDay, SystemDataFieldName.RelayStream, SystemDataFieldName.RelayStage]);

export const isRelayLayer = (dataFields: DataField[] | null | undefined) =>
    dataFields?.some((df) => df.systemDataFieldName != null && relaySystemDataFieldNames.has(df.systemDataFieldName)) ?? false;

/**
 * An imported relay layer shares its map objects with the origin stream, so it must remain read-only in the plan it
 * was imported into. A layer is one when it is linked to an origin layer (the node's sourceId is the origin layer's
 * id) and it carries a relay system data field.
 */
export const isImportedRelayLayer = (dataFields: DataField[] | undefined, node: Pick<Node, "id" | "sourceId"> | undefined) =>
    node != null && node.sourceId !== node.id && isRelayLayer(dataFields);

/** Returns a mapping from System data field name to the matching data field id  */
export const getSystemDataFieldToIdMapping = (projectDataFields: DataField[]) => ({
    [SystemDataFieldName.MapObjectName]: getMapObjectNameSystemDatafield(projectDataFields)?.id,
    [SystemDataFieldName.MapObjectArea]: getMapObjectAreaSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.MapObjectLength]: getMapObjectLengthSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.Coordinates]: getMapObjectCoordinatesSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.RouteTime]: getMapObjectRouteTimeSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.QuantityOfModelsOnLine]: getMapObjectQuantityOfModelsOnLineSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.QuantityOfModelsInArea]: getMapObjectQuantityOfModelsInAreaSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.RotationZOfModelsInArea]: getMapObjectRotationZOfModelsInAreaSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.IconRotation]: getMapObjectIconRotationSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.ModelAltitude]: getModelAltitudeSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.Relay]: getRelaySystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.RelayDay]: getRelayDaySystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.RelayStage]: getRelayStageSystemDataField(projectDataFields)?.id,
    [SystemDataFieldName.RelayStream]: getRelayStreamSystemDataField(projectDataFields)?.id,
});

/**
 * Returns the required datafields for a layer
 * @return datafield for MapObjectName
 */
export const getRequiredDataFieldsForLayer = (projectDataFields: DataField[], styleType: StyleType) => {
    const systemDataFields = getLayerSystemDataFieldsFromStyleType(styleType);
    return projectDataFields.filter((df) => (df.systemDataFieldName != null ? systemDataFields.includes(df.systemDataFieldName) : false));
};

export const getLayerSystemDataFieldsFromStyleType = (styleType: StyleType): SystemDataFieldName[] => {
    switch (styleType) {
        case StyleType.Area:
            return [SystemDataFieldName.MapObjectArea, SystemDataFieldName.MapObjectName];
        case StyleType.AreaModel:
            return [SystemDataFieldName.MapObjectArea, SystemDataFieldName.MapObjectName, SystemDataFieldName.QuantityOfModelsInArea, SystemDataFieldName.RotationZOfModelsInArea];
        case StyleType.Line:
            return [SystemDataFieldName.MapObjectLength, SystemDataFieldName.RouteTime, SystemDataFieldName.MapObjectName];
        case StyleType.LineModel:
            return [SystemDataFieldName.MapObjectLength, SystemDataFieldName.RouteTime, SystemDataFieldName.MapObjectName, SystemDataFieldName.QuantityOfModelsOnLine];
        case StyleType.Icon:
            return [SystemDataFieldName.MapObjectName, SystemDataFieldName.Coordinates, SystemDataFieldName.IconRotation];
        case StyleType.Point:
            return [SystemDataFieldName.Coordinates, SystemDataFieldName.MapObjectName];
        case StyleType.Model:
            return [SystemDataFieldName.Coordinates, SystemDataFieldName.MapObjectName, SystemDataFieldName.ModelAltitude];
        default:
            return [SystemDataFieldName.MapObjectName];
    }
};

/**
 * Given a new style type will return the layer's updated datafields (remove or add system datafields)
 */
export function updateLayerDataFieldsOnStyleTypeChange(newStyleType: StyleType, mapLayer: MapLayer, projectDataFields: DataField[]): DataField[] {
    switch (newStyleType) {
        case StyleType.LineModel: {
            const quantityDataField = getMapObjectQuantityOfModelsOnLineSystemDataField(projectDataFields);
            return [...mapLayer.dataFields, quantityDataField];
        }
        case StyleType.Line: {
            const quantityDataField = getMapObjectQuantityOfModelsOnLineSystemDataField(projectDataFields);
            return mapLayer.dataFields.filter((df) => df.id !== quantityDataField.id);
        }
        case StyleType.AreaModel: {
            const quantityDataField = getMapObjectQuantityOfModelsInAreaSystemDataField(projectDataFields);
            const rotationDataField = getMapObjectRotationZOfModelsInAreaSystemDataField(projectDataFields);
            return [
                ...mapLayer.dataFields,
                ...(mapLayer.dataFields.some((df) => df.id === quantityDataField.id) ? [] : [quantityDataField]),
                ...(mapLayer.dataFields.some((df) => df.id === rotationDataField.id) ? [] : [rotationDataField]),
            ];
        }
        case StyleType.Area: {
            const quantityDataField = getMapObjectQuantityOfModelsInAreaSystemDataField(projectDataFields);
            const rotationDataField = getMapObjectRotationZOfModelsInAreaSystemDataField(projectDataFields);
            return mapLayer.dataFields.filter((df) => df.id !== quantityDataField.id && df.id !== rotationDataField.id);
        }
        case StyleType.Icon: {
            const iconRotationDataField = getMapObjectIconRotationSystemDataField(projectDataFields);
            const modelAltitudeDataField = getModelAltitudeSystemDataField(projectDataFields);
            let updatedDataFields = removeSystemDataFieldIfPresent(mapLayer.dataFields, modelAltitudeDataField);
            updatedDataFields = addSystemDataFieldIfMissing(updatedDataFields, iconRotationDataField);
            return updatedDataFields;
        }
        case StyleType.Point: {
            const iconRotationDataField = getMapObjectIconRotationSystemDataField(projectDataFields);
            const modelAltitudeDataField = getModelAltitudeSystemDataField(projectDataFields);
            let updatedDataFields = removeSystemDataFieldIfPresent(mapLayer.dataFields, iconRotationDataField);
            updatedDataFields = removeSystemDataFieldIfPresent(updatedDataFields, modelAltitudeDataField);
            return updatedDataFields;
        }
        case StyleType.Model: {
            const modelAltitudeDataField = getModelAltitudeSystemDataField(projectDataFields);
            const iconRotationDataField = getMapObjectIconRotationSystemDataField(projectDataFields);
            let updatedDataFields = removeSystemDataFieldIfPresent(mapLayer.dataFields, iconRotationDataField);
            updatedDataFields = addSystemDataFieldIfMissing(updatedDataFields, modelAltitudeDataField);
            return updatedDataFields;
        }
        default:
            return mapLayer.dataFields;
    }
}

export function removeSystemDataFieldIfPresent(dataFields: DataField[], systemDataField: DataField) {
    if (!systemDataField) {
        return dataFields;
    }
    return dataFields.filter((df) => df.id !== systemDataField.id);
}

export function addSystemDataFieldIfMissing(dataFields: DataField[], systemDataField: DataField) {
    if (!systemDataField || dataFields.some((df) => df.id === systemDataField.id)) {
        return dataFields;
    }
    return [...dataFields, systemDataField];
}

/** Sorts datafields to ensure MapObjectName datafield is first element in the array */
export const sortDataFieldsBySystemDataField = (projectDataFields: DataField[], dataFields: DataField[]) => {
    const containsSystemDataField = projectDataFields?.some((dataField) => dataField.systemDataFieldName != null);

    // If does not contain a system datafield just return the datafields as is
    if (!containsSystemDataField) {
        return dataFields;
    }
    const projectDataFieldsMap = createMapFromArray(projectDataFields, "id");
    const mapObjectNameDataField = getMapObjectNameSystemDatafield(projectDataFields);

    return dataFields.reduce<DataField[]>((orderedDataFields, dataField) => {
        // Remove any repeated items
        if (orderedDataFields.some((df) => df.id === dataField.id)) return orderedDataFields;
        const projectDataField = projectDataFieldsMap.get(dataField.id);
        switch (projectDataField?.systemDataFieldName) {
            case SystemDataFieldName.MapObjectName:
                return [dataField, ...orderedDataFields];
            case SystemDataFieldName.MapObjectArea:
            case SystemDataFieldName.MapObjectLength:
            case SystemDataFieldName.Coordinates:
            case SystemDataFieldName.QuantityOfModelsOnLine:
            case SystemDataFieldName.IconRotation:
            case SystemDataFieldName.RouteTime: {
                if (orderedDataFields[0]?.id === mapObjectNameDataField?.id) {
                    // Find where last map object name datafield is
                    const indexOfLastMapObjectNameDataField = orderedDataFields.map(({ id }) => id).lastIndexOf(mapObjectNameDataField?.id);
                    // Spilt the datafields into map object names and other datafields
                    const mapObjectNameDataFields = orderedDataFields.slice(0, indexOfLastMapObjectNameDataField + 1);
                    const otherDataFields = orderedDataFields.slice(indexOfLastMapObjectNameDataField + 1);
                    // Place datafield after map object names and before the other datafields
                    return [...mapObjectNameDataFields, dataField, ...otherDataFields];
                }
                return [dataField, ...orderedDataFields];
            }
            default:
                return [...orderedDataFields, dataField];
        }
    }, []);
};

export const systemDataFieldConfiguration: Record<SystemDataFieldName, Pick<DataField, "name" | "type">> = {
    [SystemDataFieldName.MapObjectName]: { name: "Map Object Name", type: DataFieldType.Text },
    [SystemDataFieldName.MapObjectArea]: { name: "Area", type: DataFieldType.Number },
    [SystemDataFieldName.MapObjectLength]: { name: "Length", type: DataFieldType.Number },
    [SystemDataFieldName.Coordinates]: { name: "Coordinates", type: DataFieldType.Text },
    [SystemDataFieldName.RouteTime]: { name: "Route Time", type: DataFieldType.Time },
    [SystemDataFieldName.QuantityOfModelsOnLine]: { name: "Quantity", type: DataFieldType.Number },
    [SystemDataFieldName.QuantityOfModelsInArea]: { name: "Quantity", type: DataFieldType.Number },
    [SystemDataFieldName.RotationZOfModelsInArea]: { name: "Rotation", type: DataFieldType.Number },
    [SystemDataFieldName.IconRotation]: { name: "Rotation", type: DataFieldType.Number },
    [SystemDataFieldName.ModelAltitude]: { name: "Altitude", type: DataFieldType.Number },
    [SystemDataFieldName.Relay]: { name: "Relay", type: DataFieldType.List },
    [SystemDataFieldName.RelayDay]: { name: "Relay Day", type: DataFieldType.List },
    [SystemDataFieldName.RelayStream]: { name: "Relay Stream", type: DataFieldType.List },
    [SystemDataFieldName.RelayStage]: { name: "Relay Stage", type: DataFieldType.List },
};

export function createSystemDataField(systemDataFieldName: SystemDataFieldName): DataField {
    const { name, type } = systemDataFieldConfiguration[systemDataFieldName];

    return {
        name,
        type,
        systemDataFieldName,
        id: crypto.randomUUID(),
        customerIdentifierName: "",
        allowedMetadataProperties: [],
        listValues: [],
        projectId: undefined,
        hierarchyMetadataPropertyName: "",
        showInApi: false,
        apiNameSingular: "",
        apiNamePlural: "",
        order: 0,
        listItemProperties: [],
        defaultValue: {
            valueText: undefined,
            dataFieldId: undefined,
            listItemId: undefined,
            valueDate: undefined,
            valueNumber: undefined,
            valueTickbox: undefined,
            valueRepeatedTimeRanges: undefined,
        },
        listItemRelationships: [],
        deprecated: false,
    };
}
